// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The renderer. One class, used by the live preview on the main thread and by
 * the exporter inside a worker, so what you watch is what you get.
 *
 * It is a first-party WebGL2 renderer of about four hundred lines rather than a
 * scene graph, for the same reason meshprep's is: there is no scene. There is
 * one textured sheet, a screen-space shear and three single-pass effects.
 */

import {
  ANAGLYPH_FRAG,
  COPY_FRAG,
  DOF_FRAG,
  FULLSCREEN_VERT,
  PARALLAX_FRAG,
  PARALLAX_VERT,
} from './shaders.ts';
import type { CameraSample } from '../core/camera.ts';
import type { CoverUv } from '../core/plan.ts';

export interface DepthUpload {
  readonly data: Float32Array;
  readonly width: number;
  readonly height: number;
}

export interface FrameParams {
  readonly camera: CameraSample;
  /** Depth of the convergence plane, [0,1]. */
  readonly pivot: number;
  /** Peak-to-peak parallax for this render, as a fraction of frame width. */
  readonly amplitude: number;
  readonly cover: CoverUv;
  readonly holeMap: boolean;
}

/** Cells per side of the displaced sheet. */
export const GRID = 256;

/**
 * How much stretch counts as invention.
 *
 * Below this the triangle is merely being magnified, which every zoom does.
 * Above it, the texels being shown were never behind that part of the frame.
 */
export const HOLE_GATE = 1.5;

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('could not create a shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown error';
    gl.deleteShader(shader);
    throw new Error(`shader failed to compile: ${log}`);
  }
  return shader;
}

function link(gl: WebGL2RenderingContext, vertSrc: string, fragSrc: string): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error('could not create a program');
  const vert = compile(gl, gl.VERTEX_SHADER, vertSrc);
  const frag = compile(gl, gl.FRAGMENT_SHADER, fragSrc);
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? 'unknown error';
    gl.deleteProgram(program);
    throw new Error(`program failed to link: ${log}`);
  }
  return program;
}

function uniforms(gl: WebGL2RenderingContext, program: WebGLProgram): Record<string, WebGLUniformLocation | null> {
  const out: Record<string, WebGLUniformLocation | null> = {};
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < count; i += 1) {
    const info = gl.getActiveUniform(program, i);
    if (info) out[info.name] = gl.getUniformLocation(program, info.name);
  }
  return out;
}

/**
 * Build the sheet's index buffer, choosing each quad's diagonal.
 *
 * A uniform grid always split the same way staircases every silhouette, because
 * the tear runs at 45 degrees to the real depth edge about half the time.
 * Connecting the two corners whose depths are CLOSER puts the shared edge along
 * the surface rather than across the discontinuity, which lines the tear up
 * with the object's outline. It costs one pass over the grid and not a single
 * extra vertex.
 */
export function buildIndices(depth: DepthUpload, cover: CoverUv, grid = GRID): Uint32Array {
  const n = grid + 1;
  const out = new Uint32Array(grid * grid * 6);
  const at = (gx: number, gy: number): number => {
    const u = (gx / grid) * cover.scaleU + cover.offsetU;
    const v = (gy / grid) * cover.scaleV + cover.offsetV;
    const px = Math.min(depth.width - 1, Math.max(0, Math.round(u * depth.width - 0.5)));
    const py = Math.min(depth.height - 1, Math.max(0, Math.round(v * depth.height - 0.5)));
    return depth.data[py * depth.width + px]!;
  };
  let k = 0;
  for (let gy = 0; gy < grid; gy += 1) {
    for (let gx = 0; gx < grid; gx += 1) {
      const i00 = gy * n + gx;
      const i10 = i00 + 1;
      const i01 = i00 + n;
      const i11 = i01 + 1;
      const d00 = at(gx, gy);
      const d10 = at(gx + 1, gy);
      const d01 = at(gx, gy + 1);
      const d11 = at(gx + 1, gy + 1);
      if (Math.abs(d00 - d11) <= Math.abs(d10 - d01)) {
        out[k] = i00; out[k + 1] = i10; out[k + 2] = i11;
        out[k + 3] = i00; out[k + 4] = i11; out[k + 5] = i01;
      } else {
        out[k] = i00; out[k + 1] = i10; out[k + 2] = i01;
        out[k + 3] = i10; out[k + 4] = i11; out[k + 5] = i01;
      }
      k += 6;
    }
  }
  return out;
}

export type RenderCanvas = HTMLCanvasElement | OffscreenCanvas;

export class ParallaxRenderer {
  private readonly gl: WebGL2RenderingContext;
  private readonly parallax: WebGLProgram;
  private readonly parallaxU: Record<string, WebGLUniformLocation | null>;
  private readonly dof: WebGLProgram;
  private readonly dofU: Record<string, WebGLUniformLocation | null>;
  private readonly anaglyph: WebGLProgram;
  private readonly anaglyphU: Record<string, WebGLUniformLocation | null>;
  private readonly copy: WebGLProgram;
  private readonly copyU: Record<string, WebGLUniformLocation | null>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly indexBuffer: WebGLBuffer;
  private readonly emptyVao: WebGLVertexArrayObject;
  private photo: WebGLTexture | null = null;
  private photoSize: [number, number] = [1, 1];
  private depthTex: WebGLTexture | null = null;
  private depthSize: [number, number] = [1, 1];
  private indexCount = 0;
  private targets: { fbo: WebGLFramebuffer; tex: WebGLTexture; w: number; h: number }[] = [];

  constructor(readonly canvas: RenderCanvas) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: true,
      // Leave this at its default. The invariant that matters is that no `await`
      // sits between the draw call and the frame capture — VideoFrame
      // construction from a canvas is a synchronous snapshot, proved by drawing
      // red, constructing the frame, clearing to blue, and reading back red.
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error('This browser does not have WebGL2, which unflatten needs to draw the picture.');
    this.gl = gl;

    // R32F render targets are not needed, but float TEXTURES are, and on some
    // drivers the colour-buffer-float extension is what makes the depth upload
    // legal. Requesting it is harmless where it is already core.
    gl.getExtension('EXT_color_buffer_float');

    this.parallax = link(gl, PARALLAX_VERT, PARALLAX_FRAG);
    this.parallaxU = uniforms(gl, this.parallax);
    this.dof = link(gl, FULLSCREEN_VERT, DOF_FRAG);
    this.dofU = uniforms(gl, this.dof);
    this.anaglyph = link(gl, FULLSCREEN_VERT, ANAGLYPH_FRAG);
    this.anaglyphU = uniforms(gl, this.anaglyph);
    this.copy = link(gl, FULLSCREEN_VERT, COPY_FRAG);
    this.copyU = uniforms(gl, this.copy);

    const vao = gl.createVertexArray();
    const emptyVao = gl.createVertexArray();
    const indexBuffer = gl.createBuffer();
    if (!vao || !emptyVao || !indexBuffer) throw new Error('could not allocate GPU buffers');
    this.vao = vao;
    this.emptyVao = emptyVao;
    this.indexBuffer = indexBuffer;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bindVertexArray(null);
  }

  /**
   * Size the drawing buffer.
   *
   * Returns false when there is nothing to draw into. A canvas that has not been
   * laid out measures zero and the specification says a 0x0 canvas yields a 1x1
   * drawing buffer — draw into that and the browser stretches one pixel across
   * the element the moment it becomes visible, which reads as a camera bug and
   * has been diagnosed as one in this fleet before.
   */
  resize(width: number, height: number): boolean {
    if (width < 2 || height < 2) return false;
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    return this.gl.drawingBufferWidth >= 2 && this.gl.drawingBufferHeight >= 2;
  }

  setPhoto(bitmap: ImageBitmap): void {
    const gl = this.gl;
    if (this.photo) gl.deleteTexture(this.photo);
    const tex = gl.createTexture();
    if (!tex) throw new Error('could not allocate the photo texture');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // SRGB8_ALPHA8 so the sampler linearises on read. Every stage after this —
    // the depth-of-field gather, the anaglyph matrix — is only correct in
    // linear light.
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.SRGB8_ALPHA8, bitmap.width, bitmap.height);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, bitmap.width, bitmap.height, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.photo = tex;
    this.photoSize = [bitmap.width, bitmap.height];
  }

  setDepth(depth: DepthUpload, cover: CoverUv): void {
    const gl = this.gl;
    if (this.depthTex) gl.deleteTexture(this.depthTex);
    const tex = gl.createTexture();
    if (!tex) throw new Error('could not allocate the depth texture');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.R32F, depth.width, depth.height);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, depth.width, depth.height, gl.RED, gl.FLOAT, depth.data);
    // NEAREST is not a quality choice. WebGL2 promotes OES_texture_float to
    // core but explicitly not OES_texture_float_linear, so LINEAR on an R32F
    // texture is not available; the shader taps it bilinearly by hand.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.depthTex = tex;
    this.depthSize = [depth.width, depth.height];

    const indices = buildIndices(depth, cover);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    this.indexCount = indices.length;
  }

  private bindTextures(program: WebGLProgram, u: Record<string, WebGLUniformLocation | null>): void {
    const gl = this.gl;
    gl.useProgram(program);
    if (u.uPhoto) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.photo);
      gl.uniform1i(u.uPhoto, 0);
    }
    if (u.uDepth) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.depthTex);
      gl.uniform1i(u.uDepth, 1);
    }
    if (u.uDepthSize) gl.uniform2f(u.uDepthSize, this.depthSize[0], this.depthSize[1]);
    if (u.uPhotoSize) gl.uniform2f(u.uPhotoSize, this.photoSize[0], this.photoSize[1]);
  }

  /** Draw one frame of the parallax clip into the currently bound target. */
  drawFrame(params: FrameParams): void {
    const gl = this.gl;
    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;
    gl.viewport(0, 0, w, h);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);
    gl.clearColor(0, 0, 0, 1);
    gl.clearDepth(1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    this.bindTextures(this.parallax, this.parallaxU);
    const u = this.parallaxU;
    const aspect = w / h;
    // The camera's shift is a fraction of frame WIDTH; normalised device
    // coordinates span 2 across the width and 2 across the height, so the
    // vertical component has to be scaled by the aspect ratio or a square
    // orbit comes out as an ellipse.
    if (u.uShift) {
      gl.uniform2f(u.uShift, params.camera.tx * 2, params.camera.ty * 2 * aspect);
    }
    if (u.uPivot) gl.uniform1f(u.uPivot, params.pivot);
    if (u.uZoom) gl.uniform1f(u.uZoom, params.camera.zoom);
    // Same units as the lateral shift: a fraction of frame width, doubled into
    // normalised device coordinates.
    if (u.uDepthZoom) gl.uniform1f(u.uDepthZoom, params.camera.dz * 2);
    if (u.uGrid) gl.uniform1i(u.uGrid, GRID);
    if (u.uUvScale) gl.uniform2f(u.uUvScale, params.cover.scaleU, params.cover.scaleV);
    if (u.uUvOffset) gl.uniform2f(u.uUvOffset, params.cover.offsetU, params.cover.offsetV);
    if (u.uHoleMap) gl.uniform1f(u.uHoleMap, params.holeMap ? 1 : 0);
    if (u.uHoleGate) gl.uniform1f(u.uHoleGate, HOLE_GATE);
    if (u.uBaseMag) {
      // Screen pixels per source texel with no stretch at all — what a
      // legitimate zoom does, so the overlay does not light up the whole frame
      // just because the camera pushed in.
      const cropTexels = this.photoSize[0] * params.cover.scaleU;
      gl.uniform1f(u.uBaseMag, (w / Math.max(1, cropTexels)) * params.camera.zoom);
    }

    gl.bindVertexArray(this.vao);
    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_INT, 0);
    gl.bindVertexArray(null);
    gl.disable(gl.DEPTH_TEST);
  }

  /** Render the photo with a depth-graded defocus, into the default target. */
  drawDepthOfField(cover: CoverUv, focus: number, strengthPx: number): void {
    const gl = this.gl;
    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;
    gl.viewport(0, 0, w, h);
    gl.disable(gl.DEPTH_TEST);
    this.bindTextures(this.dof, this.dofU);
    const u = this.dofU;
    if (u.uUvScale) gl.uniform2f(u.uUvScale, cover.scaleU, cover.scaleV);
    if (u.uUvOffset) gl.uniform2f(u.uUvOffset, cover.offsetU, cover.offsetV);
    if (u.uPixel) gl.uniform2f(u.uPixel, 1 / w, 1 / h);
    if (u.uFocus) gl.uniform1f(u.uFocus, focus);
    if (u.uStrength) gl.uniform1f(u.uStrength, strengthPx);
    gl.bindVertexArray(this.emptyVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }

  /** An off-screen colour target, reused between calls at the same size. */
  private target(index: number, w: number, h: number) {
    const gl = this.gl;
    const existing = this.targets[index];
    if (existing && existing.w === w && existing.h === h) return existing;
    if (existing) {
      gl.deleteFramebuffer(existing.fbo);
      gl.deleteTexture(existing.tex);
    }
    const tex = gl.createTexture();
    const fbo = gl.createFramebuffer();
    if (!tex || !fbo) throw new Error('could not allocate a render target');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, w, h);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const depthBuffer = gl.createRenderbuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, depthBuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, w, h);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depthBuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const made = { fbo, tex, w, h };
    this.targets[index] = made;
    return made;
  }

  /**
   * The red/cyan pair, combined with the Dubois projection.
   *
   * Each eye is a normal parallax frame at half the excursion in opposite
   * directions, with the convergence plane pinned to the focal plane so the
   * subject sits at the screen and everything else falls behind the window.
   */
  drawAnaglyph(cover: CoverUv, pivot: number, amplitude: number): void {
    const gl = this.gl;
    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;
    const eyes: WebGLTexture[] = [];
    for (let e = 0; e < 2; e += 1) {
      const t = this.target(e, w, h);
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
      this.drawFrame({
        camera: { tx: (e === 0 ? -1 : 1) * (amplitude / 2), ty: 0, zoom: 1, dz: 0 },
        pivot,
        amplitude,
        cover,
        holeMap: false,
      });
      eyes.push(t.tex);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    gl.disable(gl.DEPTH_TEST);
    gl.useProgram(this.anaglyph);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, eyes[0]!);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, eyes[1]!);
    if (this.anaglyphU.uLeft) gl.uniform1i(this.anaglyphU.uLeft, 0);
    if (this.anaglyphU.uRight) gl.uniform1i(this.anaglyphU.uRight, 1);
    gl.bindVertexArray(this.emptyVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }

  /** Present whatever is in a texture. Only used by the tests. */
  blit(texture: WebGLTexture): void {
    const gl = this.gl;
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.useProgram(this.copy);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    if (this.copyU.uSource) gl.uniform1i(this.copyU.uSource, 0);
    gl.bindVertexArray(this.emptyVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this.gl;
    if (this.photo) gl.deleteTexture(this.photo);
    if (this.depthTex) gl.deleteTexture(this.depthTex);
    for (const t of this.targets) {
      gl.deleteFramebuffer(t.fbo);
      gl.deleteTexture(t.tex);
    }
    this.targets = [];
    gl.deleteBuffer(this.indexBuffer);
    gl.deleteVertexArray(this.vao);
    gl.deleteVertexArray(this.emptyVao);
    gl.deleteProgram(this.parallax);
    gl.deleteProgram(this.dof);
    gl.deleteProgram(this.anaglyph);
    gl.deleteProgram(this.copy);
  }
}
